import { inject, Injectable } from '@angular/core';
import { FormNode } from '@app/core/model/other/field-config';
import { ImportTemplate, UpdateImportTemplateInput } from '@app/core/model/other/import-template';
import { removeTypenameProperty } from '@app/shared/extra/utils';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { IRowNode } from 'ag-grid-community';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { DocumentNode } from 'graphql';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class ImportTemplateService {

  private readonly importTemplateGraphQlFragment: DocumentNode;

  private appManager: AppManager = inject(AppManager);
  private apiService = inject(ApiService);

  constructor() {
    this.importTemplateGraphQlFragment = gql`
      fragment ImportTemplateInfo on ImportTemplate {
        id
        entityType
        columnGroupsList {
          code
          order
          label
          columnsList {
            fieldCode
            order
            customOptions
            field {
              code
              label
              entityType
              fieldType
              computed
              parentPathList
              fieldValuesList
              validatorsList {
                conditionsList {
                  field
                  operator
                  value
                }
                definition
                type
              }
            }
            conditionsToViewList {
              field
              operator
              value
            }
            conditionsToEditList {
              field
              operator
              value
            }
          }
        }
      }
    `;
  }

  /**
   * Get all ImportTemplates of an organization.
   * The importTemplates are then flattened to calculate the data path for Ag-Grid tree
   * @return Observable that emits a list of FormNodes.
   */
  public getAllFlattenedFormNodes(): Observable<FormNode[]> {
    const query = gql`
      query AllImportTemplates($orgId: String!) {
        importTemplates(organizationId: $orgId) {
          ...ImportTemplateInfo
        }
      }
      ${this.importTemplateGraphQlFragment}
    `;
    const variables = {
      orgId: this.appManager.currentOrganization.id
    };
    return this.apiService.query({query, variables}).pipe(
      map(data => plainToInstance(ImportTemplate, data['importTemplates'] as ImportTemplate[])),
      map(importTemplates => this.flattenChildrenRecursively(importTemplates))
    );
  }

  /**
   * Updates a importTemplate given an input of a few properties
   * @param importTemplateId Id of the importTemplate to update
   * @param importTemplateInput Input containing a new label or new order
   * @return Observable containing the newly updated importTemplate with its updated column group
   */
  public updateImportTemplate(importTemplateId: string, importTemplateInput: UpdateImportTemplateInput): Observable<FormNode[]> {
    const mutation = gql`
      mutation UpdateImportTemplate($updateImportTemplateInput: UpdateImportTemplateInput!) {
        updateImportTemplate(importTemplateInput: $updateImportTemplateInput) {
          ...ImportTemplateInfo
        }
      }
      ${this.importTemplateGraphQlFragment}
    `;
    const variables = {
      updateImportTemplateInput: {id: importTemplateId, ...importTemplateInput}
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(ImportTemplate, data['updateImportTemplate'])),
        map(forms => this.flattenChildrenRecursively([forms]))
      );
  }

  /**
   * Deletes a form node from the import template datagrid
   * @param nodeToDelete the Ag-Grid row node to remove along with its children
   * @return Observable containing response if the operation was successful
   */
  public deleteImportTemplateFormNodes(nodeToDelete: IRowNode): Observable<FormNode[]> {
    const mutation = gql`
      mutation DeleteFormNodes($nodePath: [String]!, $organizationId: String!) {
        deleteImportTemplateFormNodes(organizationId: $organizationId, nodePath: $nodePath) {
          ...ImportTemplateInfo
        }
      }
      ${this.importTemplateGraphQlFragment}
    `;
    const variables = {
      nodePath: nodeToDelete.data.hierarchy,
      organizationId: this.appManager.currentOrganization.id
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(ImportTemplate, data['deleteImportTemplateFormNodes'])),
        map(forms => this.flattenChildrenRecursively([forms]))
      );
  }

  /**
   * Flattens the children of a form node recursively and creates a list of FormNodes
   * containing the data path from the root FormNode to all children
   * @param data the form nodes to flatten
   * @param parent the parent node, if present
   * @param childHierarchy the hierarchy data structure to build
   * @return a list of FormNodes (importTemplates, columnGroups, columns) containing the hierarchy path from the root FormNode to all children
   */
  private flattenChildrenRecursively(data?: FormNode[], parent = null, childHierarchy = null): FormNode[] {
    let newData = [];
    if (!data) return newData; // Abort early if data is undefined

    data.forEach(initialRow => {
      let parentHierarchy = [];
      initialRow.hierarchy = parentHierarchy;

      if (parent) {
        initialRow['parent'] = parent;
        parentHierarchy = [...childHierarchy];
        initialRow.hierarchy = parentHierarchy;
      }

      newData.push(initialRow);
      switch (initialRow['graphQlTypeName']) {
        case 'ImportTemplate':
          parentHierarchy.push(initialRow['entityType']);
          newData = [
            ...newData,
            ...this.flattenChildrenRecursively(initialRow['children'], initialRow, parentHierarchy)
          ];
          break;
        case 'ColumnGroup':
          parentHierarchy.push(initialRow['code']);
          newData = [
            ...newData,
            ...this.flattenChildrenRecursively(initialRow['children'], initialRow, parentHierarchy)
          ];
          break;
        case 'FieldConfig':
          parentHierarchy.push(initialRow['fieldCode']);
          break;
        default:
          break;
      }
    });
    return newData;
  }

  /**
   * Converts an ImportTemplate object to an UpdateImportTemplateInput object. Only keeps the relevant properties to send downstream.
   * @param importTemplate the ImportTemplate object to convert
   * @return the ImportTemplateInput object
   */
  public dataToImportTemplateInput(importTemplate: ImportTemplate): UpdateImportTemplateInput {
    return removeTypenameProperty({
      entityType: importTemplate.entityType,
      customOptions: importTemplate.customOptions,
      columnGroups: importTemplate.children.map(group => ({
        code: group.code,
        label: group.label,
        order: group.order,
        customOptions: group.customOptions,
        columns: group.children.map(config => ({
          fieldCode: config.fieldCode,
          order: config.order,
          customOptions: config.customOptions,
          conditionsToView: config.conditionsToView,
          conditionsToEdit: config.conditionsToEdit
        }))
      }))
    });
  }
}
